// mapSchema.js — ProseMirror schema for map data-item documents.
// This file owns only the schema definition and basic document utilities.
// Collaboration plugin setup lives in mapCollaborationPlugins.js.
// Adapter utilities (extractMapState, mapDocFromFragment, etc.) live in mapPmAdapter.js.

import {Schema} from 'prosemirror-model';
import {history} from 'prosemirror-history';
import {keymap} from 'prosemirror-keymap';
import {undo, redo} from 'prosemirror-history';
import {EditorState} from 'prosemirror-state';
import {generateUUID} from '../../prosemirror-editor/helpers/utils';

// ponytail: OpenFreeMap only — no API key, no CORS, no rate limits.
// Replaces CARTO positron which fails on deployed envs (egress blocks / referer checks).
export const BASEMAP_STYLES = [
    {id: 'liberty',  label: 'Liberty',  url: 'https://tiles.openfreemap.org/styles/liberty'},
    {id: 'bright',   label: 'Bright',   url: 'https://tiles.openfreemap.org/styles/bright'},
    {id: 'positron', label: 'Positron', url: 'https://tiles.openfreemap.org/styles/positron'},
    {id: 'fiord',    label: 'Fiord',    url: 'https://tiles.openfreemap.org/styles/fiord'},
];

export const DEFAULT_BASEMAP = BASEMAP_STYLES[0].url;

export const DEFAULT_VIEW_ID = '__all_markings__';

/** Pixel padding when fitting to the "All markings" extent (not used for named crop views). */
export const MAP_FIT_PADDING = 48;

// ticket 01: the map editor's "Overlay" selector — 'mapbox' (default 4-style vector picker)
// or 'satellite' (NASA GIBS raster imagery, driven by the live DTG cursor). Kept as a plain
// string union rather than a boolean so a later ticket could add more overlay kinds without
// another attr/migration.
export const OVERLAY_TYPES = {MAPBOX: 'mapbox', SATELLITE: 'satellite'};

const mapMetaNode = {
    content: '',
    group: 'block',
    atom: true,
    attrs: {
        // shared-map-data ticket 02: the report's ONE shared DTG timeline range — every map node
        // embed's full-screen editor shows/edits the same `[dtgStartMs, dtgEndMs]` window (unlike
        // `dtgSelectedMs`, which stays a per-embed attr on mapNode itself — see mapNode.js). Null
        // defaults mean "never edited yet"; MapEditorModal/useDtgRange fall back to their own
        // default 30-day window in that case (same defaults as before this ticket).
        //
        // shared-map-data ticket 03: `title`/`basemapStyleId`/`activeGridId`/`overlayType`/
        // `currentDtgMs` used to live here too, but moved to mapNode's own attrs (report doc) —
        // each embed picks its own basemap/grid/overlay/title/satellite-cursor "window"
        // independently, even though the layers/markings/views/DTG-range above stay shared. This
        // node is left in place (rather than dropped entirely) purely to hold that shared range;
        // there's currently nothing else shared-but-not-layer/marking/view-shaped to put here.
        dtgStartMs: {default: null},
        dtgEndMs: {default: null},
    },
    parseDOM: [{
        tag: 'div[data-type=map-meta]',
        getAttrs(dom) {
            const dtgStartRaw = dom.getAttribute('data-dtg-start-ms');
            const dtgEndRaw = dom.getAttribute('data-dtg-end-ms');
            const dtgStartParsed = dtgStartRaw ? parseInt(dtgStartRaw, 10) : NaN;
            const dtgEndParsed = dtgEndRaw ? parseInt(dtgEndRaw, 10) : NaN;
            return {
                dtgStartMs: Number.isFinite(dtgStartParsed) ? dtgStartParsed : null,
                dtgEndMs: Number.isFinite(dtgEndParsed) ? dtgEndParsed : null,
            };
        },
    }],
    toDOM(node) {
        return ['div', {
            'data-type': 'map-meta',
            'data-dtg-start-ms': node.attrs.dtgStartMs != null ? String(node.attrs.dtgStartMs) : '',
            'data-dtg-end-ms': node.attrs.dtgEndMs != null ? String(node.attrs.dtgEndMs) : '',
        }];
    },
};

const mapLayerNode = {
    content: '',
    group: 'block',
    atom: true,
    attrs: {
        layerId: {default: null},
        label: {default: 'Layer'},
        visible: {default: true},
        order: {default: 0},
        status: {default: 'working'}, // 'working' | 'approved'
        // ticket 01 (layer-panel-rework): who created this layer — same uid derivation as
        // marking `author`/`updatedBy` (see mapSchema.js's mapMarkingNode). `null` means
        // "unowned" (legacy layers created before this attr existed) — treated as owned by
        // everyone, same as today's pre-ownership behavior.
        ownerId: {default: null},
    },
    parseDOM: [{
        tag: 'div[data-type=map-layer]',
        getAttrs(dom) {
            return {
                layerId: dom.getAttribute('data-layer-id'),
                label: dom.getAttribute('data-label') || 'Layer',
                visible: dom.getAttribute('data-visible') !== 'false',
                order: parseInt(dom.getAttribute('data-order') || '0', 10),
                status: dom.getAttribute('data-status') || 'working',
                ownerId: dom.getAttribute('data-owner-id') || null,
            };
        },
    }],
    toDOM(node) {
        const {layerId, label, visible, order, status, ownerId} = node.attrs;
        return ['div', {
            'data-type': 'map-layer',
            'data-layer-id': layerId,
            'data-label': label,
            'data-visible': visible ? 'true' : 'false',
            'data-order': String(order ?? 0),
            'data-status': status || 'working',
            'data-owner-id': ownerId || '',
        }];
    },
};

/**
 * Derive elementType from the legacy `mode` value when upgrading old markings.
 * ponytail: one-way migration; old mode string kept for back-compat reads.
 */
function elementTypeFromMode(mode) {
    switch (mode) {
        case 'marker': return 'unit-icon';
        case 'point': return 'point';
        case 'linestring': return 'free-line';
        case 'polygon':
        case 'rectangle':
        case 'freehand': return 'free-polygon';
        default: return 'free-polygon';
    }
}

const mapMarkingNode = {
    content: '',
    group: 'block',
    atom: true,
    attrs: {
        markingId: {default: null},
        layerId: {default: null},
        mode: {default: 'polygon'},
        // v2 fields -------------------------------------------------------
        // elementType: coarser shape category used by the new renderer.
        // 'unit-icon' | 'tactical-line' | 'tactical-area' | 'point' | 'free-polygon' | 'free-line'
        elementType: {default: null},
        // -----------------------------------------------------------------
        // ponytail (ticket 03): `author` is kept as-is (not renamed to `createdBy`) so already-
        // persisted Yjs documents don't silently lose their authorship data on load — renaming a
        // PM/Yjs attr key is a lossy migration for any doc that doesn't get re-saved through new
        // code. Semantically this field IS "createdBy" (who created this marking); `updatedBy`
        // below is the new, genuinely-new "who last edited it" field. New code should read/write
        // `author` and treat/label it as createdBy; do not add a second author-at-creation field.
        author: {default: null},
        createdAt: {default: null},
        updatedAt: {default: null},
        // ticket 03: who last edited this marking (same uid derivation as `author`/createdBy).
        // Paired with `updatedAt`, which already existed but was never stamped with a "who".
        updatedBy: {default: null},
        // ticket 03: stable identity generated once at creation and never changed by edits —
        // unlike `markingId` (a PM node identity), `trackId` is the domain "this is the same
        // real-world symbol over time" identity ticket 04/06 key off of.
        trackId: {default: null},
        // ticket 03: 'stationary' | 'moving' | null — auto-derived from the SIDC's symbol set at
        // creation time (see getMobilityForSidc in renderer/SymbolRenderer.js), user-overridable
        // via the ContextPanel. null means "undetermined / manual" (e.g. no SIDC, or a symbol set
        // getMobilityForSidc can't classify).
        mobility: {default: null},
        // ticket 01: sole source of truth for ALL time-varying render data — append-only
        // history of `{dtg, geometry, props}`, where `props` carries every rendering-relevant
        // field that can change over time: `sidc`, `sidcModifiers`, `label`, `affiliation`
        // (MIL-STD-2525E / APP-6 20-digit SIDC + JSON modifiers + display label + derived
        // affiliation). There is deliberately NO top-level cache of any of these — reads for
        // "current" (non-trail) rendering must resolve the live snapshot via
        // `resolveTrackAtDtg`/`markingsToGeoJSON` (see mapPmAdapter.js) rather than reading a
        // marking-level attr. Every marking-creation call site writes exactly one snapshot at
        // creation time, so this should never actually be null/empty in practice — `null` is
        // only the structural PM default before the node's initial `create()` call supplies it.
        snapshots: {default: null},
        graphicType: {default: null},   // tactical graphic type: 'phase-line' | 'boundary' | 'feba' | etc.
    },
    parseDOM: [{
        tag: 'div[data-type=map-marking]',
        getAttrs(dom) {
            // ticket 03/01: absent on legacy DOM nodes → default to null/empty (matches attr
            // defaults). ticket 01: this is now the ONLY place geometry/sidc/sidcModifiers/label
            // data lives — there's no top-level fallback to parse alongside it.
            let snapshots = null;
            try {
                snapshots = JSON.parse(dom.getAttribute('data-snapshots') || 'null');
            } catch (_) {
                snapshots = null;
            }
            const mode = dom.getAttribute('data-mode') || 'polygon';
            // ponytail: migrate old markings — elementType absent → derive from mode.
            const elementType = dom.getAttribute('data-element-type') || elementTypeFromMode(mode);
            return {
                markingId: dom.getAttribute('data-marking-id'),
                layerId: dom.getAttribute('data-layer-id'),
                mode,
                elementType,
                author: dom.getAttribute('data-author') || null,
                createdAt: dom.getAttribute('data-created-at')
                    ? parseInt(dom.getAttribute('data-created-at'), 10) : null,
                updatedAt: dom.getAttribute('data-updated-at')
                    ? parseInt(dom.getAttribute('data-updated-at'), 10) : null,
                updatedBy: dom.getAttribute('data-updated-by') || null,
                trackId: dom.getAttribute('data-track-id') || null,
                mobility: dom.getAttribute('data-mobility') || null,
                snapshots,
                graphicType: dom.getAttribute('data-graphic-type') || null,
            };
        },
    }],
    toDOM(node) {
        const a = node.attrs;
        return ['div', {
            'data-type': 'map-marking',
            'data-marking-id': a.markingId,
            'data-layer-id': a.layerId,
            'data-mode': a.mode,
            'data-element-type': a.elementType || elementTypeFromMode(a.mode),
            'data-author': a.author || '',
            'data-created-at': a.createdAt != null ? String(a.createdAt) : '',
            'data-updated-at': a.updatedAt != null ? String(a.updatedAt) : '',
            'data-updated-by': a.updatedBy || '',
            'data-track-id': a.trackId || '',
            'data-mobility': a.mobility || '',
            'data-snapshots': a.snapshots ? JSON.stringify(a.snapshots) : '',
            'data-graphic-type': a.graphicType || '',
        }];
    },
};

const mapViewNode = {
    content: '',
    group: 'block',
    atom: true,
    attrs: {
        viewId: {default: null},
        name: {default: 'View'},
        bbox: {default: null},
    },
    parseDOM: [{
        tag: 'div[data-type=map-view]',
        getAttrs(dom) {
            let bbox = null;
            try {
                bbox = JSON.parse(dom.getAttribute('data-bbox') || 'null');
            } catch (_) {
                bbox = null;
            }
            return {
                viewId: dom.getAttribute('data-view-id'),
                name: dom.getAttribute('data-name') || 'View',
                bbox,
            };
        },
    }],
    toDOM(node) {
        return ['div', {
            'data-type': 'map-view',
            'data-view-id': node.attrs.viewId,
            'data-name': node.attrs.name,
            'data-bbox': JSON.stringify(node.attrs.bbox),
        }];
    },
};

export const mapSchema = new Schema({
    nodes: {
        doc: {content: 'block*'},
        // Required by ProseMirror even though map docs are atom-only blocks.
        text: {group: 'inline'},
        map_meta: mapMetaNode,
        map_layer: mapLayerNode,
        map_marking: mapMarkingNode,
        map_view: mapViewNode,
    },
    marks: {},
});

/** Empty map document JSON for new data items (includes a default layer). */
export function createEmptyMapDocJSON() {
    const layerId = newLayerId();
    return {
        type: 'doc',
        content: [
            {
                type: 'map_meta',
                attrs: {},
            },
            {
                type: 'map_layer',
                attrs: {
                    layerId,
                    label: 'Layer 1',
                    visible: true,
                    order: 0,
                },
            },
        ],
    };
}

/** Build an EditorState from PM JSON. */
export function mapStateFromJSON(json) {
    const doc = json
        ? mapSchema.nodeFromJSON(json)
        : mapSchema.nodeFromJSON(createEmptyMapDocJSON());
    return EditorState.create({
        schema: mapSchema,
        doc,
        plugins: [
            history(),
            keymap({'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo}),
        ],
    });
}

/** Serialize EditorState to PM JSON. */
export function mapStateToJSON(state) {
    return state.doc.toJSON();
}

export function newLayerId() {
    return generateUUID();
}

export function newMarkingId() {
    return generateUUID();
}

/**
 * ticket 03: stable per-symbol "domain identity" — generated once at marking creation
 * and never regenerated on edit. Distinct from `markingId` (the PM node identity) so a
 * future re-creation/undo-redo/split flow could, in principle, preserve trackId continuity
 * independently of markingId. Ticket 04/06 key time-aware rendering/filtering off this.
 */
export function newTrackId() {
    return generateUUID();
}

/**
 * ticket 03/01: build one entry of a marking's append-only `snapshots` history — the sole
 * source of truth for a marking's time-varying render data (there is no top-level cache attr
 * mirroring any of this; see the `snapshots` attr doc comment above).
 *
 * @param {number} dtg - epoch ms for this snapshot. Sourced from the "current DTG cursor"
 *   value (see useDtgRange.js / ticket 01) at the moment of creation/edit; callers that don't
 *   have a live cursor wired in yet should pass `Date.now()`.
 * @param {object|null} geometry - GeoJSON geometry at this point in time.
 * @param {object} [props] - every other rendering-relevant property that can change over time:
 *   `label`, `sidc`, `sidcModifiers`, `affiliation` (derived from `sidc` by the caller — this
 *   helper doesn't know about mil-sym), plus any editor-specific patch fields callers choose to
 *   remember. Plain JSON-serializable object.
 * @returns {{dtg: number, geometry: object|null, props: object}}
 */
export function newMarkingSnapshot(dtg, geometry, props = {}) {
    return {dtg, geometry: geometry ?? null, props: props || {}};
}

export function newViewId() {
    return generateUUID();
}

// -- Basemap tile URL helpers --------------------------------------------------
// ponytail: OFM doesn't publish a plain XYZ endpoint; these are the closest public
// equivalents. All styles default to OFM Liberty MVT; positron/fiord fall back to CARTO.
export const OFM_XYZ_URL = 'https://tiles.openfreemap.org/planet/{z}/{x}/{y}';

export function basemapToXyz(styleUrl) {
    if (!styleUrl) return OFM_XYZ_URL;
    if (styleUrl.includes('positron')) return 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';
    if (styleUrl.includes('fiord'))    return 'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png';
    return OFM_XYZ_URL;
}
